// src/components/settings/BuyCreditsModal.tsx

import React, { useState, useEffect, useRef } from 'react';
import { 
  X, Zap, Crown, Gem, Star, Loader2, ShieldCheck, 
  CheckCircle, ArrowRight, AlertCircle, Coins, 
  CreditCard, Smartphone, Globe, Wallet
} from 'lucide-react';
import { UserProfile } from '../../types';
import { db } from '../../services/firebase';
import { doc, updateDoc, increment, collection, addDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';

interface BuyCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile: UserProfile;
  onPurchase?: (packageId: string, newCredits: number) => Promise<void>;
}

// 🟢 Firebase Storage URLs for payment logos
const PAYMENT_LOGOS = {
  yoco: 'https://firebasestorage.googleapis.com/v0/b/scoop-streetwear.firebasestorage.app/o/yoco-logo.png?alt=media',
  paypal: 'https://firebasestorage.googleapis.com/v0/b/scoop-streetwear.firebasestorage.app/o/paypal-logo.png?alt=media',
  makapay: 'https://firebasestorage.googleapis.com/v0/b/scoop-streetwear.firebasestorage.app/o/makapay-logo.png?alt=media',
  applePay: 'https://firebasestorage.googleapis.com/v0/b/scoop-streetwear.firebasestorage.app/o/ApplePayLogo.png?alt=media&token=a3b8ef0b-7755-4598-a6c1-37844db68d91'
};

// 🟢 Custom Paystack hook (FIXED)
const usePaystack = () => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    // Check if Paystack is already loaded
    const checkPaystack = () => {
      if (window.PaystackPop) {
        setLoaded(true);
        setError(null);
        return true;
      }
      return false;
    };

    // If already loaded, set state and return
    if (checkPaystack()) return;

    // Check if script already exists
    const existingScript = document.querySelector('script[src*="paystack"]');
    if (existingScript) {
      // Wait for it to load
      const interval = setInterval(() => {
        if (window.PaystackPop) {
          setLoaded(true);
          setError(null);
          clearInterval(interval);
        }
      }, 100);
      return () => clearInterval(interval);
    }

    // Create and load script
    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    script.id = 'paystack-script';
    
    script.onload = () => {
      if (window.PaystackPop) {
        setLoaded(true);
        setError(null);
      } else {
        setError('Paystack failed to initialize');
      }
    };
    
    script.onerror = () => {
      setError('Failed to load Paystack SDK');
    };
    
    document.head.appendChild(script);

    return () => {
      // Don't remove the script as it might be used elsewhere
    };
  }, []);
  
  return { loaded, error };
};

// 🟢 Yoco Hook
const useYoco = () => {
  const [loaded, setLoaded] = useState(false);
  const [yocoInstance, setYocoInstance] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const initYoco = () => {
      if (window.YocoSDK) {
        setLoaded(true);
        try {
          const publicKey = getEnvVar('YOCO_PUBLIC_KEY') || 'pk_live_818cf81bLdmKEK88b504';
          if (publicKey) {
            setYocoInstance(new window.YocoSDK({ publicKey }));
          } else {
            setError('Yoco public key not configured');
          }
        } catch (err: any) { 
          console.error('Error initializing Yoco:', err);
          setError('Failed to initialize Yoco');
        }
      }
    };

    if (window.YocoSDK) {
      initYoco();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://js.yoco.com/sdk/v1/yoco-sdk.js';
    script.async = true;
    script.onload = initYoco;
    document.body.appendChild(script);
  }, []);
  
  return { loaded, yocoInstance, error };
};

// 🟢 PayPal Hook
const usePayPal = () => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    if (window.paypal) { 
      setLoaded(true); 
      return; 
    }
    
    const clientId = getEnvVar('PAYPAL_CLIENT_ID') || 'BAAi7IaER2dnUXQaSeYAJHBNUJTX0PmIxTCNaamgSLeL-6Kk7FgYuwi4JLM0n5VnpJGH_msNgMZyIIIaBM';
    if (!clientId) { 
      setError('PayPal client ID not configured');
      return; 
    }
    
    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=USD&intent=capture`;
    script.async = true;
    script.onload = () => { 
      if (window.paypal) setLoaded(true); 
    };
    script.onerror = () => {
      setError('Failed to load PayPal SDK');
    };
    document.body.appendChild(script);
  }, []);
  
  return { loaded, error };
};

declare global {
  interface Window {
    PaystackPop: any;
    YocoSDK: any;
    paypal: any;
  }
}

// Helper to get env variables
const getEnvVar = (key: string): string => {
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return import.meta.env[`VITE_${key}`] || import.meta.env[`REACT_APP_${key}`] || '';
  }
  return process.env[`REACT_APP_${key}`] || process.env[`VITE_${key}`] || '';
};

export const BuyCreditsModal: React.FC<BuyCreditsModalProps> = ({
  isOpen,
  onClose,
  profile,
  onPurchase
}) => {
  const { loaded: paystackLoaded, error: paystackError } = usePaystack();
  const { loaded: yocoLoaded, yocoInstance, error: yocoError } = useYoco();
  const { loaded: paypalLoaded, error: paypalError } = usePayPal();
  
  const [selectedPackageId, setSelectedPackageId] = useState('scooper');
  const [customAmount, setCustomAmount] = useState(''); 
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [currentBalance, setCurrentBalance] = useState(profile?.cred || profile?.credits || 0);
  const [selectedMethod, setSelectedMethod] = useState<'card' | 'yoco' | 'paypal' | 'makapay' | 'apple' | 'payshap'>('card');
  const [paystackHandler, setPaystackHandler] = useState<any>(null);

  const userId = profile?.uid || profile?.id;
  const paystackTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Real-time balance
  useEffect(() => {
    if (!isOpen || !userId) return;
    const userRef = doc(db, 'users', userId);
    const unsubscribe = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setCurrentBalance(data.cred || data.credits || 0);
      }
    });
    return () => unsubscribe();
  }, [isOpen, userId]);

  // Cleanup Paystack handler on unmount
  useEffect(() => {
    return () => {
      if (paystackTimeoutRef.current) {
        clearTimeout(paystackTimeoutRef.current);
      }
    };
  }, []);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  // Packages (compact)
  const packages = [
    { id: 'starter', name: 'Starter', price: 99, credits: 109, icon: Zap, bonus: '+10' },
    { id: 'scooper', name: 'Scooper', price: 449, credits: 500, icon: Crown, bonus: '+51', popular: true },
    { id: 'pro', name: 'Pro', price: 849, credits: 1000, icon: Gem, bonus: '+151' },
    { id: 'vip', name: 'VIP', price: 3999, credits: 5000, icon: Star, bonus: '+1001' }
  ];

  const getPaystackKey = (): string => {
    let key = '';
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      key = import.meta.env.VITE_PAYSTACK_PUBLIC_KEY || '';
    }
    if (!key) key = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || '';
    // Fallback to test/live key
    if (!key) key = 'pk_live_7985b67599e69e640b5f64f870c93b8c77208617';
    return key;
  };

  const getTransactionDetails = () => {
    if (customAmount) {
      const amount = parseInt(customAmount) || 0;
      return { price: amount, credits: amount, packageId: 'custom' };
    }
    const pkg = packages.find(p => p.id === selectedPackageId) || packages[1];
    return { price: pkg.price, credits: pkg.credits, packageId: selectedPackageId };
  };

  const { price, credits, packageId } = getTransactionDetails();

  // ============================================
  // 🟢 PAYMENT EXECUTION
  // ============================================
  const executeCheckout = () => {
    if (customAmount) {
      const amount = parseInt(customAmount);
      if (!customAmount || isNaN(amount) || amount < 10) {
        showToast("Minimum is R10");
        return;
      }
      if (amount > 10000) {
        showToast("Maximum is R10,000");
        return;
      }
    }

    // Route to appropriate payment method
    if (selectedMethod === 'yoco') {
      executeYocoPayment();
    } else if (selectedMethod === 'paypal') {
      executePayPalPayment();
    } else if (selectedMethod === 'makapay') {
      executeMakaPayPayment();
    } else {
      executePaystackPayment();
    }
  };

  // ============================================
  // 🟢 PAYSTACK PAYMENT (FIXED)
  // ============================================
  const executePaystackPayment = () => {
    // Check if Paystack is loaded
    if (!paystackLoaded && !window.PaystackPop) {
      if (paystackError) {
        showToast("Paystack error: " + paystackError);
      } else {
        showToast("Paystack is loading. Please wait...");
      }
      return;
    }

    // Use window.PaystackPop if available
    const Paystack = window.PaystackPop;
    if (!Paystack) {
      showToast("Paystack not available. Please refresh.");
      return;
    }

    setIsProcessing(true);
    setIsMinimized(true);

    const publicKey = getPaystackKey();
    if (!publicKey) {
      showToast("Payment system not configured");
      setIsProcessing(false);
      setIsMinimized(false);
      return;
    }

    const reference = `CRD_${Date.now()}_${Math.random().toString(36).substring(7)}`;

    // Map selected method to Paystack channels
    let channels = ['card'];
    if (selectedMethod === 'apple') channels = ['mobile_money'];
    if (selectedMethod === 'payshap') channels = ['bank_transfer', 'eft', 'ussd'];

    // Clear any existing timeout
    if (paystackTimeoutRef.current) {
      clearTimeout(paystackTimeoutRef.current);
    }

    // Set timeout for payment
    paystackTimeoutRef.current = setTimeout(() => {
      setIsProcessing(false);
      setIsMinimized(false);
      showToast("Payment timed out");
    }, 600000);

    try {
      const handler = Paystack.setup({
        key: publicKey,
        email: profile?.email || "user@scoop.app",
        amount: Math.round(price * 100),
        currency: 'ZAR',
        ref: reference,
        channels: channels,
        metadata: {
          custom_fields: [
            { display_name: "Package", variable_name: "package", value: packageId },
            { display_name: "Credits", variable_name: "credits", value: credits.toString() }
          ]
        },
        callback: async (response: any) => {
          // Clear timeout
          if (paystackTimeoutRef.current) {
            clearTimeout(paystackTimeoutRef.current);
            paystackTimeoutRef.current = null;
          }
          
          console.log('Paystack callback response:', response);
          
          // Check if payment was successful
          if (response.status === 'success' || response.message === 'Approved') {
            setIsMinimized(false);
            await handlePaymentSuccess(response, credits, price);
          } else {
            setIsProcessing(false);
            setIsMinimized(false);
            showToast(`Payment failed: ${response.message || 'Unknown error'}`);
          }
        },
        onClose: () => {
          // Clear timeout
          if (paystackTimeoutRef.current) {
            clearTimeout(paystackTimeoutRef.current);
            paystackTimeoutRef.current = null;
          }
          
          // Only reset if still processing (not already successful)
          if (isProcessing) {
            setIsProcessing(false);
            setIsMinimized(false);
            showToast("Payment cancelled");
          }
        }
      });

      // Store handler for potential cleanup
      setPaystackHandler(handler);
      
      // Open the Paystack popup
      handler.openIframe();
      
    } catch (error: any) {
      console.error('Paystack error:', error);
      if (paystackTimeoutRef.current) {
        clearTimeout(paystackTimeoutRef.current);
        paystackTimeoutRef.current = null;
      }
      setIsProcessing(false);
      setIsMinimized(false);
      showToast("Payment error: " + (error.message || 'Unknown error'));
    }
  };

  // ============================================
  // 🟢 YOCO PAYMENT
  // ============================================
  const executeYocoPayment = async () => {
    if (!yocoLoaded || !yocoInstance) {
      showToast("Yoco is loading. Please wait...");
      return;
    }
    if (yocoError) {
      showToast("Yoco error: " + yocoError);
      return;
    }

    setIsProcessing(true);
    setIsMinimized(true);
    showToast("Opening Yoco secure payment...");

    try {
      const result = await yocoInstance.showPopup({
        amountInCents: Math.round(price * 100),
        currency: 'ZAR',
        name: 'Scoop Credits',
        description: `${credits} Credits - ${packageId}`,
        metadata: {
          orderId: `CRD_${Date.now()}`,
          customerEmail: profile?.email,
          package: packageId,
          credits: credits.toString()
        }
      });

      console.log('Yoco result:', result);

      if (result && result.id) {
        showToast("✅ Yoco payment successful!");
        await handlePaymentSuccess({ reference: result.id, status: 'success' }, credits, price);
      } else {
        setIsProcessing(false);
        setIsMinimized(false);
        showToast("Yoco payment failed. Please try again.");
      }
    } catch (e: any) {
      console.error('Yoco error:', e);
      setIsProcessing(false);
      setIsMinimized(false);
      showToast("Yoco error: " + (e.message || 'Unknown error'));
    }
  };

  // ============================================
  // 🟢 PAYPAL PAYMENT
  // ============================================
  const executePayPalPayment = () => {
    if (!paypalLoaded) {
      showToast("PayPal is loading. Please wait...");
      return;
    }
    if (paypalError) {
      showToast("PayPal error: " + paypalError);
      return;
    }

    setIsProcessing(true);
    setIsMinimized(true);
    showToast("Opening PayPal...");

    let container = document.getElementById('paypal-button-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'paypal-button-container';
      document.body.appendChild(container);
    }
    container.style.display = 'block';

    const cleanupPayPal = () => {
      if (container) {
        container.innerHTML = '';
        container.style.display = 'none';
      }
    };

    // Convert ZAR to USD for PayPal (~18.5 ZAR/USD)
    const usdTotal = (price / 18.5).toFixed(2);

    try {
      window.paypal.Buttons({
        style: { layout: 'vertical', color: 'black', shape: 'rect', label: 'paypal' },
        createOrder: (data: any, actions: any) => {
          return actions.order.create({
            purchase_units: [{ 
              amount: { value: usdTotal, currency_code: 'USD' },
              description: `${credits} Scoop Credits - ${packageId}`,
              custom_id: `CRD_${Date.now()}`,
            }],
            application_context: { shipping_preference: 'NO_SHIPPING' }
          });
        },
        onApprove: (data: any, actions: any) => {
          return actions.order.capture().then(async (details: any) => {
            cleanupPayPal();
            if (details.status === 'COMPLETED') {
              showToast("✅ PayPal payment successful!");
              await handlePaymentSuccess({ reference: details.id, status: 'success' }, credits, price);
            } else {
              setIsProcessing(false);
              setIsMinimized(false);
              showToast("PayPal payment failed.");
            }
          });
        },
        onCancel: () => {
          cleanupPayPal();
          setIsProcessing(false);
          setIsMinimized(false);
          showToast("PayPal payment cancelled.");
        },
        onError: (err: any) => {
          cleanupPayPal();
          setIsProcessing(false);
          setIsMinimized(false);
          showToast("PayPal error: " + (err.message || 'Unknown error'));
        }
      }).render('#paypal-button-container');
    } catch (error: any) {
      cleanupPayPal();
      setIsProcessing(false);
      setIsMinimized(false);
      showToast("PayPal error: " + (error.message || 'Unknown error'));
    }
  };

  // ============================================
  // 🟢 MAKAPAY PAYMENT
  // ============================================
  const executeMakaPayPayment = () => {
    const merchantId = getEnvVar('MAKAPAY_MERCHANT_ID') || '5790977235';
    const makapayUrl = getEnvVar('MAKAPAY_URL') || 'https://makapay.co.za/pay';
    
    if (!merchantId) {
      showToast('MakaPay merchant ID not configured');
      return;
    }

    setIsProcessing(true);
    setIsMinimized(true);
    showToast("Redirecting to MakaPay...");

    const orderId = `CRD_${Date.now()}`;
    const returnUrl = `${window.location.origin}${window.location.pathname}?credits=success`;
    const cancelUrl = `${window.location.origin}${window.location.pathname}?credits=cancelled`;

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = makapayUrl;
    form.target = '_blank';
    
    const fields = {
      merchant_id: merchantId,
      order_id: orderId,
      amount: price.toFixed(2),
      currency: 'ZAR',
      customer_email: profile?.email || 'user@scoop.app',
      customer_name: profile?.displayName || 'Scoop User',
      return_url: returnUrl,
      cancel_url: cancelUrl,
      description: `${credits} Scoop Credits - ${packageId}`,
      metadata: JSON.stringify({ 
        package: packageId, 
        credits: credits.toString(),
        userId: userId 
      })
    };

    Object.entries(fields).forEach(([key, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = String(value);
      form.appendChild(input);
    });

    document.body.appendChild(form);
    
    // For demo: simulate success after redirect
    // In production, handle the return URL callback
    setTimeout(() => {
      if (form.parentNode) form.parentNode.removeChild(form);
      // Simulate payment success after 5 seconds (remove in production)
      setTimeout(() => {
        handlePaymentSuccess({ reference: orderId, status: 'success' }, credits, price);
      }, 5000);
    }, 1000);
    
    form.submit();
  };

  // ============================================
  // 🟢 HANDLE PAYMENT SUCCESS
  // ============================================
  const handlePaymentSuccess = async (reference: any, creditsToAdd: number, amountPaid: number) => {
    try {
      if (!userId) throw new Error("No user session");

      console.log('Processing payment success:', { reference, creditsToAdd, amountPaid });

      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { cred: increment(creditsToAdd) });

      await addDoc(collection(db, 'users', userId, 'transactions'), {
        type: 'credit_purchase',
        amount: amountPaid,
        creditsAdded: creditsToAdd,
        package: packageId,
        reference: reference.reference || reference.id || 'unknown',
        method: selectedMethod,
        createdAt: serverTimestamp(),
        status: 'completed'
      });

      setCurrentBalance(prev => prev + creditsToAdd);
      if (onPurchase) await onPurchase(packageId, currentBalance + creditsToAdd);

      setPaymentSuccess(true);
      setIsProcessing(false);
      setIsMinimized(false);
      showToast(`✅ Added ${creditsToAdd} Credits!`);
      
      setTimeout(() => {
        setPaymentSuccess(false);
        onClose();
      }, 2000);
    } catch (error) {
      console.error("Fulfillment Error:", error);
      showToast("Payment succeeded but update failed. Contact support.");
      setIsProcessing(false);
      setIsMinimized(false);
    }
  };

  if (!isOpen) return null;

  // Minimized state
  if (isMinimized && !paymentSuccess) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-[100] bg-black/90 backdrop-blur-md border-t-2 border-zinc-700 p-4 flex items-center justify-between animate-in slide-in-from-bottom-full duration-300 shadow-[0_-10px_30px_rgba(0,0,0,0.8)]">
        <div className="flex items-center gap-3">
          <Loader2 size={24} className="animate-spin text-white" />
          <div>
            <span className="text-white font-black text-xs uppercase tracking-widest">Processing...</span>
            <p className="text-zinc-500 text-[8px] font-mono uppercase">Complete payment in popup</p>
          </div>
        </div>
        <button 
          onClick={() => {
            // Close Paystack iframe if open
            if (paystackHandler && typeof paystackHandler.close === 'function') {
              paystackHandler.close();
            }
            setIsProcessing(false);
            setIsMinimized(false);
            showToast("Cancelled");
          }}
          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-black text-xs uppercase tracking-widest rounded-lg transition-all"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center pointer-events-none p-4">
      
      {/* Toast */}
      {toastMessage && (
        <div className={`absolute top-4 left-1/2 -translate-x-1/2 z-[9999] px-4 py-2 rounded-full font-black text-[10px] uppercase tracking-widest flex items-center gap-2 shadow-2xl animate-in slide-in-from-top-6 fade-in duration-300 ${
          toastMessage.includes('✅') ? 'bg-green-600 border border-green-500 text-white' : 'bg-red-600 border border-red-500 text-white'
        }`}>
          {toastMessage.includes('✅') ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
          {toastMessage}
        </div>
      )}

      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/90 backdrop-blur-md pointer-events-auto animate-in fade-in duration-300" onClick={onClose} />

      {/* Modal - Compact */}
      <div className="bg-black w-full max-w-md max-h-[90vh] flex flex-col pointer-events-auto animate-in zoom-in-95 duration-300 relative border-2 border-zinc-800 rounded-2xl overflow-hidden">
        
        {/* Header - Compact */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800 bg-black shrink-0">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-black text-white uppercase italic tracking-tighter">Buy Credits</h2>
            <span className="text-[10px] text-zinc-500 font-mono">Balance: <span className="text-white font-bold">{currentBalance}</span></span>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center border border-zinc-800 text-zinc-500 hover:text-white hover:bg-zinc-900 transition-colors rounded-lg">
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-hide">
          
          {paymentSuccess ? (
            <div className="flex flex-col items-center justify-center py-8 animate-in zoom-in-95 duration-300">
              <CheckCircle size={56} className="text-white mb-4" strokeWidth={1.5} />
              <h2 className="text-3xl font-black text-white uppercase italic tracking-tighter">Cleared!</h2>
              <div className="mt-3 border-2 border-white px-4 py-2 bg-white text-black font-black text-sm rounded-lg">
                + {credits} CRD
              </div>
            </div>
          ) : (
            <>
              {/* Package Grid - Compact 2x2 */}
              <div className="grid grid-cols-2 gap-2">
                {packages.map((pkg) => {
                  const isSelected = selectedPackageId === pkg.id;
                  const Icon = pkg.icon;
                  return (
                    <button 
                      key={pkg.id}
                      onClick={() => setSelectedPackageId(pkg.id)}
                      className={`relative p-3 rounded-xl border-2 transition-all text-left ${
                        isSelected ? 'bg-white border-white' : 'bg-zinc-950 border-zinc-800 hover:border-zinc-600'
                      }`}
                    >
                      {pkg.popular && (
                        <span className="absolute -top-1 -right-1 text-[6px] font-black bg-yellow-500 text-black px-1.5 py-0.5 rounded-full uppercase tracking-wider">
                          ★
                        </span>
                      )}
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${isSelected ? 'border-black text-black' : 'border-zinc-800 text-zinc-500'}`}>
                          <Icon size={16} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1">
                            <span className={`font-black text-sm ${isSelected ? 'text-black' : 'text-white'}`}>
                              {pkg.credits}
                            </span>
                            {pkg.bonus && (
                              <span className={`text-[6px] font-black px-1.5 py-0.5 rounded border ${isSelected ? 'border-black text-black' : 'border-green-500 text-green-400'}`}>
                                {pkg.bonus}
                              </span>
                            )}
                          </div>
                          <p className={`text-[8px] font-mono uppercase ${isSelected ? 'text-zinc-800' : 'text-zinc-500'}`}>
                            R{pkg.price}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Custom Amount - Compact */}
              <div className="flex items-center gap-2 bg-zinc-950 border border-zinc-800 rounded-xl p-2">
                <span className="text-white font-black text-sm pl-1">R</span>
                <input 
                  type="number" 
                  placeholder="Custom" 
                  min="10"
                  max="10000"
                  className="bg-transparent text-white font-bold text-sm outline-none w-full placeholder:text-zinc-600"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                />
                {customAmount && (
                  <span className="text-zinc-500 text-[10px] font-mono whitespace-nowrap">
                    = {parseInt(customAmount) || 0} CRD
                  </span>
                )}
              </div>

              {/* Payment Methods - With Firebase Logos */}
              <div className="grid grid-cols-3 gap-2">
                {/* Card / Paystack */}
                <button
                  onClick={() => setSelectedMethod('card')}
                  className={`p-2 rounded-xl border-2 flex flex-col items-center justify-center gap-1 transition-all ${
                    selectedMethod === 'card' 
                      ? 'bg-white border-white text-black' 
                      : 'bg-zinc-950 border-zinc-800 text-zinc-500 hover:border-zinc-600'
                  }`}
                >
                  <CreditCard size={16} />
                  <span className="text-[6px] font-black uppercase tracking-widest">Card</span>
                  {!paystackLoaded && !window.PaystackPop && (
                    <span className="text-[5px] text-yellow-500">loading...</span>
                  )}
                </button>

                {/* Yoco - Firebase Logo */}
                <button
                  onClick={() => setSelectedMethod('yoco')}
                  className={`p-2 rounded-xl border-2 flex flex-col items-center justify-center gap-1 transition-all ${
                    selectedMethod === 'yoco' 
                      ? 'bg-[#4A90D9] border-[#4A90D9] text-white' 
                      : 'bg-zinc-950 border-zinc-800 text-zinc-500 hover:border-[#4A90D9] hover:text-white'
                  }`}
                >
                  <div className="w-8 h-6 flex items-center justify-center">
                    <img 
                      src={PAYMENT_LOGOS.yoco} 
                      alt="Yoco" 
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const parent = e.currentTarget.parentElement;
                        if (parent) {
                          const fallback = document.createElement('span');
                          fallback.className = 'font-black text-xs tracking-tighter text-white';
                          fallback.textContent = 'YOCO';
                          parent.appendChild(fallback);
                        }
                      }}
                    />
                  </div>
                  <span className="text-[6px] font-black uppercase tracking-widest">Yoco</span>
                </button>

                {/* PayPal - Firebase Logo */}
                <button
                  onClick={() => setSelectedMethod('paypal')}
                  className={`p-2 rounded-xl border-2 flex flex-col items-center justify-center gap-1 transition-all ${
                    selectedMethod === 'paypal' 
                      ? 'bg-[#0070BA] border-[#0070BA] text-white' 
                      : 'bg-zinc-950 border-zinc-800 text-zinc-500 hover:border-[#0070BA] hover:text-white'
                  }`}
                >
                  <div className="w-8 h-6 flex items-center justify-center">
                    <img 
                      src={PAYMENT_LOGOS.paypal} 
                      alt="PayPal" 
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const parent = e.currentTarget.parentElement;
                        if (parent) {
                          const fallback = document.createElement('span');
                          fallback.className = 'font-black text-xs tracking-tighter text-white';
                          fallback.textContent = 'PayPal';
                          parent.appendChild(fallback);
                        }
                      }}
                    />
                  </div>
                  <span className="text-[6px] font-black uppercase tracking-widest">PayPal</span>
                </button>

                {/* MakaPay - Firebase Logo */}
                <button
                  onClick={() => setSelectedMethod('makapay')}
                  className={`p-2 rounded-xl border-2 flex flex-col items-center justify-center gap-1 transition-all ${
                    selectedMethod === 'makapay' 
                      ? 'bg-[#FF6B00] border-[#FF6B00] text-white' 
                      : 'bg-zinc-950 border-zinc-800 text-zinc-500 hover:border-[#FF6B00] hover:text-white'
                  }`}
                >
                  <div className="w-8 h-6 flex items-center justify-center">
                    <img 
                      src={PAYMENT_LOGOS.makapay} 
                      alt="MakaPay" 
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const parent = e.currentTarget.parentElement;
                        if (parent) {
                          const fallback = document.createElement('span');
                          fallback.className = 'font-black text-xs tracking-tighter text-white';
                          fallback.textContent = 'Maka';
                          parent.appendChild(fallback);
                        }
                      }}
                    />
                  </div>
                  <span className="text-[6px] font-black uppercase tracking-widest">MakaPay</span>
                </button>

                {/* Apple Pay - Firebase Logo */}
                <button
                  onClick={() => setSelectedMethod('apple')}
                  className={`p-2 rounded-xl border-2 flex flex-col items-center justify-center gap-1 transition-all ${
                    selectedMethod === 'apple' 
                      ? 'bg-black border-white text-white' 
                      : 'bg-zinc-950 border-zinc-800 text-zinc-500 hover:border-white hover:text-white'
                  }`}
                >
                  <div className="w-8 h-6 flex items-center justify-center bg-white rounded">
                    <img 
                      src={PAYMENT_LOGOS.applePay} 
                      alt="Apple Pay" 
                      className="w-full h-full object-contain p-0.5"
                      onError={(e) => { 
                        e.currentTarget.style.display = 'none';
                        const parent = e.currentTarget.parentElement;
                        if (parent) {
                          const fallback = document.createElement('span');
                          fallback.className = 'font-black text-[8px] tracking-tighter text-black';
                          fallback.textContent = 'Apple';
                          parent.appendChild(fallback);
                        }
                      }}
                    />
                  </div>
                  <span className="text-[6px] font-black uppercase tracking-widest">Apple</span>
                </button>

                {/* PayShap */}
                <button
                  onClick={() => setSelectedMethod('payshap')}
                  className={`p-2 rounded-xl border-2 flex flex-col items-center justify-center gap-1 transition-all ${
                    selectedMethod === 'payshap' 
                      ? 'bg-white border-white text-black' 
                      : 'bg-zinc-950 border-zinc-800 text-zinc-500 hover:border-zinc-600'
                  }`}
                >
                  <Globe size={16} />
                  <span className="text-[6px] font-black uppercase tracking-widest">PayShap</span>
                </button>
              </div>

              {/* Summary - Compact */}
              <div className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-xl px-3 py-2">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={12} className="text-green-500" />
                  <span className="text-[8px] text-zinc-500 font-mono uppercase">Secured via {selectedMethod.toUpperCase()}</span>
                </div>
                <div className="text-right">
                  <span className="text-[8px] text-zinc-500 font-mono uppercase">Total</span>
                  <span className="text-white font-black text-sm ml-2">R{price}</span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer - Compact */}
        {!paymentSuccess && (
          <div className="p-3 border-t border-zinc-800 bg-black shrink-0">
            <button 
              onClick={executeCheckout} 
              disabled={isProcessing || (customAmount && (parseInt(customAmount) < 10 || parseInt(customAmount) > 10000))}
              className="w-full py-3 bg-white text-black rounded-xl font-black text-xs uppercase tracking-widest hover:bg-zinc-200 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isProcessing ? (
                <Loader2 className="animate-spin" size={16}/>
              ) : (
                <>Pay R{price} <ArrowRight size={14}/></>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};